import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import MuiToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { useParams } from 'react-router-dom';
import { GiftCardValidationStatusForCompany, ValidationStatus } from '../../types/MetricsValidationStatusForCompany';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined';
import TableContainer from '@mui/material/TableContainer';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { useState, useMemo } from 'react';
import { keyBy } from 'lodash';

import { styled } from '@mui/material';
import { useFetchGiftCardValidationStatus } from '../common/hooks/useFetchGiftcardValidationStatus';
import { useListCompaniesName } from '../common/hooks/useListCompaniesName';
import { Company } from '../../services/FinaloopGql';

const ToggleButton = styled(MuiToggleButton)({
  '&.Mui-selected, &.Mui-selected:hover': {
    color: 'black',
    backgroundColor: 'LightGrey',
  },
});
interface ReportsTableProps {
  reports: GiftCardValidationStatusForCompany[];
  companies: Required<Pick<Company, 'id' | 'name'>>[];
}

interface CompanyIntegrationInfo {
  companyId: string;
  integrationAccountId: string;
}

interface ReportTableRowProps {
  report: GiftCardValidationStatusForCompany;
  companyName: string;
  rowIndex: number;
}

enum Version {
  V1 = 'v1',
  V2 = 'v2',
}

const ReportsTableRow = ({ report, companyName, rowIndex }: ReportTableRowProps) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      <TableRow sx={{ '& > *': { borderBottom: 'unset' } }}>
        <TableCell>
          <IconButton aria-label="expand row" size="small" onClick={() => setIsOpen(!isOpen)}>
            {isOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell>{rowIndex}</TableCell>
        <TableCell component="th" scope="row">
          {companyName}
          {report.errMsg != null && (
            <>
              <br />
              <span style={{ fontSize: 12, color: 'red' }}>{report.errMsg}</span>
            </>
          )}
        </TableCell>
        <TableCell>{report.companyId}</TableCell>
        <TableCell>{report.integrationAccountId}</TableCell>
        <TableCell>{report.createdAt}</TableCell>
        <TableCell>{report.endTime}</TableCell>
        <TableCell>
          {report.status === ValidationStatus.SUCCESS ? (
            <CheckCircleOutlineOutlinedIcon color="success" />
          ) : (
            <ErrorOutlineOutlinedIcon color="error" />
          )}
        </TableCell>
        <TableCell>{report.shopifyBalance}</TableCell>
        <TableCell>{report.shopifyExpiredBalance}</TableCell>
        <TableCell>{report.finaloopBalance}</TableCell>
        <TableCell>{report.gap}</TableCell>
      </TableRow>
    </>
  );
};

const ReportsTable = ({ reports, companies }: ReportsTableProps) => {
  const [sorter, setSorter] = useState(() => (a: any, b: any) => a.companyName?.localeCompare(b.companyName));
  const [sortDirection, setSortDirection] = useState(true);
  const [currentVersion, setCurrentVersion] = useState(Version.V1);

  const companyById = useMemo(() => {
    if (!companies) {
      return {};
    }
    return keyBy(companies, 'id');
  }, [companies]);

  const getNumberSorterFunc = (extractProperty: (a: any) => number) => {
    return (a: any, b: any) => ((extractProperty(a) ?? -Infinity) > (extractProperty(b) ?? -Infinity) ? 1 : -1);
  };

  const getStringSorterFunc = (extractProperty: (a: any) => string) => {
    return (a: any, b: any) => (extractProperty(a) || '').localeCompare(extractProperty(b) || '');
  };

  const sortedReports = useMemo(() => {
    console.info('currentVersion', currentVersion);
    const filteredReport = reports.filter(
      (report) => !report.version || report.version.toLowerCase() === currentVersion.toLowerCase(),
    );
    if (!filteredReport) {
      return [];
    }
    const extendedReports = filteredReport.map((report) => ({
      ...report,
      companyName: companyById[report.companyId]?.name,
    }));
    const sortReportsInit = extendedReports.sort(sorter);
    return sortDirection ? sortReportsInit : sortReportsInit.reverse();
  }, [reports, sorter, sortDirection, companyById, currentVersion]);

  return (
    <>
      <ToggleButtonGroup
        color="primary"
        value={currentVersion}
        exclusive
        onChange={(event, newVersion) => {
          console.info('newVersion', newVersion);
          setCurrentVersion(newVersion);
        }}
        aria-label="Platform"
      >
        <ToggleButton value={Version.V1}>{Version.V1}</ToggleButton>
        <ToggleButton value={Version.V2}>{Version.V2}</ToggleButton>
      </ToggleButtonGroup>
      <TableContainer component={Paper}>
        <Table stickyHeader sx={{ minWidth: 650 }}>
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell>#</TableCell>
              <TableCell
                sx={{ cursor: 'pointer' }}
                onClick={() => {
                  setSortDirection(!sortDirection);
                  setSorter(() => getStringSorterFunc((a: any) => a.companyName));
                }}
              >
                Company
              </TableCell>
              <TableCell
                sx={{ cursor: 'pointer' }}
                onClick={() => {
                  setSortDirection(!sortDirection);
                  setSorter(() => getStringSorterFunc((a: any) => a.companyId));
                }}
              >
                CompanyId
              </TableCell>
              <TableCell
                sx={{ cursor: 'pointer' }}
                onClick={() => {
                  setSortDirection(!sortDirection);
                  setSorter(() => getStringSorterFunc((a: any) => a.integrationAccountId));
                }}
              >
                Shop Name
              </TableCell>
              <TableCell
                sx={{ cursor: 'pointer' }}
                onClick={() => {
                  setSortDirection(!sortDirection);
                  setSorter(() => getStringSorterFunc((a: any) => a.createdAt));
                }}
              >
                Created At
              </TableCell>
              <TableCell
                sx={{ cursor: 'pointer' }}
                onClick={() => {
                  setSortDirection(!sortDirection);
                  setSorter(() => getStringSorterFunc((a: any) => a.endTime));
                }}
              >
                End Time
              </TableCell>
              <TableCell
                sx={{ cursor: 'pointer' }}
                onClick={() => {
                  setSortDirection(!sortDirection);
                  setSorter(() => (a: any, b: any) => a.status.localeCompare(b.status));
                }}
              >
                Status
              </TableCell>
              <TableCell
                sx={{ cursor: 'pointer' }}
                onClick={() => {
                  setSortDirection(!sortDirection);
                  setSorter(() => getNumberSorterFunc((a) => a.shopifyBalance));
                }}
              >
                Shopify Balance
              </TableCell>

              <TableCell
                sx={{ cursor: 'pointer' }}
                onClick={() => {
                  setSortDirection(!sortDirection);
                  setSorter(() => getNumberSorterFunc((a) => a.shopifyExpiredBalance));
                }}
              >
                Shopify Expired Balance
              </TableCell>
              <TableCell
                sx={{ cursor: 'pointer' }}
                onClick={() => {
                  setSortDirection(!sortDirection);
                  setSorter(() => getNumberSorterFunc((a) => a.finaloopBalance));
                }}
              >
                Finaloop Balance
              </TableCell>
              <TableCell
                sx={{ cursor: 'pointer' }}
                onClick={() => {
                  setSortDirection(!sortDirection);
                  setSorter(() => getNumberSorterFunc((a) => a.gap));
                }}
              >
                Gap
              </TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedReports.map((report, index) => {
              const company = companyById[report.companyId];
              if (!company) return null;

              return (
                <ReportsTableRow
                  rowIndex={index}
                  key={`${report.companyId}-${report.integrationAccountId}`}
                  report={report}
                  companyName={company.name}
                />
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

export const GiftCardsValidation = () => {
  const { integrationType } = useParams();
  const listCompanies = useListCompaniesName();

  const giftCardValidationStatus = useFetchGiftCardValidationStatus(integrationType);
  if (listCompanies.isLoading || giftCardValidationStatus.loading) {
    return <>Loading...</>;
  }

  return <ReportsTable reports={giftCardValidationStatus.data} companies={listCompanies.data || []} />;
};
